import { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import JobCosting from "@/components/JobCosting";
import JobChecklist from "@/components/JobChecklist";
import CrewAssignment from "@/components/CrewAssignment";
import LiveRollout from "@/components/LiveRollout";
import AssetCapture from "@/components/AssetCapture";
import ProjectShareBar from "@/components/ProjectShareBar";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Navigation, Mic, PenTool, DollarSign, FileCheck2, Plus, Trash2, MapPin,
  Camera, Download, RefreshCw, ListChecks, Save,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, Eyebrow, Spinner } from "@/components/tac";
import { runOrQueue } from "@/lib/offline";
import { generateBinder, binderDataUri } from "@/lib/pdf";
import JobPhotoCapture from "@/components/JobPhotoCapture";

const NEXT = { scheduled: "en_route", en_route: "in_progress", in_progress: "needs_review", needs_review: "completed" };
const SAMPLE = "Balanced the panel, moved four circuits, installed a whole-home surge protector. Used two 20 amp breakers and one surge device. About three and a half hours. Customer wants a quote for a subpanel next visit.";

function SignaturePad({ onCapture }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const strokeLen = useRef(0);
  useEffect(() => {
    const c = ref.current; const ctx = c.getContext("2d");
    ctx.fillStyle = "#0d1420"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2.5; ctx.lineCap = "round";
  }, []);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };
  const start = (e) => { drawing.current = true; const { x, y } = pos(e); const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = ref.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); strokeLen.current += 1; };
  // Reject a blank/one-tap signature: require a real stroke of meaningful length.
  const end = () => { drawing.current = false; onCapture(strokeLen.current >= 8 ? ref.current.toDataURL("image/png") : ""); };
  const clear = () => { const c = ref.current; const ctx = c.getContext("2d"); ctx.fillStyle = "#0d1420"; ctx.fillRect(0, 0, c.width, c.height); strokeLen.current = 0; onCapture(""); };
  return (
    <div>
      <canvas ref={ref} width={440} height={160} data-testid="field-signature-pad-canvas"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full touch-none rounded-xl border border-slate-700" />
      <button onClick={clear} data-testid="signature-clear-btn" className="mt-2 text-xs text-slate-400 hover:text-white">Clear signature</button>
    </div>
  );
}

function PayoutWidget({ jobId, payout, onSaved }) {
  const [amount, setAmount] = useState(payout?.amount ?? "");
  const [busy, setBusy] = useState(false);
  const paid = payout?.status === "paid";
  const save = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/jobs/${jobId}/payout`, { amount: Number(amount) || 0, status });
      toast.success(status === "paid" ? "Payout marked paid" : "Payout saved");
      onSaved?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="card-tac mt-4 flex flex-wrap items-center gap-3 p-4" data-testid="job-payout-widget">
      <div className="flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><DollarSign className="h-4 w-4 text-emerald-400" /> Sub payout</div>
      <span className="text-slate-500 text-sm">$</span>
      <input data-testid="payout-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
        className="w-28 rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-1.5 text-sm text-white focus:border-emerald-500 focus:outline-none" />
      {paid
        ? <span data-testid="payout-paid-badge" className="font-mono-tac text-xs text-emerald-400">Paid{payout?.paid_at ? ` · ${String(payout.paid_at).slice(0, 10)}` : ""}</span>
        : payout?.amount ? <span className="font-mono-tac text-xs text-amber-400">Due</span> : null}
      <div className="ml-auto flex gap-2">
        <button data-testid="payout-save-btn" disabled={busy} onClick={() => save("pending")} className="rounded-full border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:border-cyan-500 disabled:opacity-50">Save</button>
        {paid
          ? <button data-testid="payout-unpay-btn" disabled={busy} onClick={() => save("pending")} className="rounded-full border border-emerald-600/50 px-4 py-1.5 text-xs text-emerald-300 disabled:opacity-50">Undo paid</button>
          : <button data-testid="payout-markpaid-btn" disabled={busy || !amount} onClick={() => save("paid")} className="btn-amber rounded-full px-4 py-1.5 text-xs disabled:opacity-50">Mark paid</button>}
      </div>
    </div>
  );
}


export default function JobDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canOffice = ["owner", "dispatcher"].includes(user?.role);

  const { data, isLoading } = useQuery({ queryKey: ["job", id], queryFn: async () => (await api.get(`/jobs/${id}`)).data });
  const { data: media } = useQuery({ queryKey: ["media", id], queryFn: async () => (await api.get(`/jobs/${id}/media`)).data });
  const { data: template } = useQuery({ queryKey: ["template", data?.job?.trade], enabled: !!data?.job?.trade, queryFn: async () => (await api.get(`/trades/${data.job.trade}/template`)).data });
  const { data: pretaskTpl } = useQuery({ queryKey: ["pretask-template", data?.job?.trade], enabled: !!data?.job, queryFn: async () => (await api.get(`/pretask-template?trade=${data.job.trade || ""}`)).data });

  const [transcript, setTranscript] = useState("");
  const [lines, setLines] = useState([{ description: "", qty: 1, unit_price: 0 }]);
  const [signer, setSigner] = useState("");
  const [sig, setSig] = useState("");
  const [payKind, setPayKind] = useState("deposit");
  const [payAmt, setPayAmt] = useState(0);
  const [binderBusy, setBinderBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceLang, setVoiceLang] = useState("en-US");
  const [checklist, setChecklist] = useState({});
  const [sysState, setSysState] = useState({});
  const [pretask, setPretask] = useState({});
  const [crewLead, setCrewLead] = useState("");
  const [pretaskSig, setPretaskSig] = useState("");
  const fileRef = useRef(null);
  const recognitionRef = useRef(null);

  const refetch = () => { qc.invalidateQueries({ queryKey: ["job", id] }); qc.invalidateQueries({ queryKey: ["media", id] }); };
  useEffect(() => { if (data?.job) { setChecklist(data.job.checklist || {}); setSysState(data.job.system_state || {}); setPretask(data.job.pretask_lock?.items || {}); setCrewLead(data.job.pretask_lock?.crew_lead || ""); } }, [data?.job?.id]);
  if (isLoading) return <div className="p-8"><Spinner /></div>;
  const { job, estimates, ai_drafts, payments } = data;

  const advance = async () => {
    const next = NEXT[job.status];
    if (!next) return;
    const geo = ["en_route", "in_progress"].includes(next) ? { lat: 30.2672, lng: -97.7431 } : {};
    try {
      const live = await runOrQueue("job_status", { job_id: id, status: next, ...geo },
        () => api.patch(`/jobs/${id}/status`, { status: next, ...geo }));
      toast[live ? "success" : "info"](live ? `Moved to ${next.replace(/_/g, " ")}` : "Offline - status queued");
      if (live) refetch();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const safety = async () => {
    const live = await runOrQueue("safety_gate", { job_id: id }, () => api.post(`/jobs/${id}/safety-gate`));
    toast[live ? "success" : "info"](live ? "Safety gate cleared" : "Offline - safety gate queued");
    if (live) refetch();
  };
  const completePretask = async () => {
    if (!crewLead.trim()) return toast.error("Enter the crew lead name");
    if (!pretaskSig) return toast.error("Capture the crew lead signature");
    try { await api.post(`/jobs/${id}/pretask`, { items: pretask, crew_lead: crewLead, signature: pretaskSig }); toast.success("Pre-task safety locked in"); refetch(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const submitVoice = async () => {
    if (!transcript.trim()) return toast.error("Record or paste a debrief first");
    try { await api.post("/ai/voice-debrief", { job_id: id, transcript }); toast.success("Sent to review queue"); setTranscript(""); refetch(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggleRecord = (auto = false) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Voice capture unsupported here - use Simulate or type the debrief");
    if (recording) { recognitionRef.current?.stop(); setRecording(false); return; }
    const rec = new SR();
    rec.lang = voiceLang; rec.continuous = true; rec.interimResults = true;
    let finalTxt = transcript ? transcript + " " : "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalTxt += t + " "; else interim += t;
      }
      setTranscript((finalTxt + interim).trim());
    };
    rec.onerror = () => setRecording(false);
    rec.onend = async () => {
      setRecording(false);
      if (auto) {
        const text = finalTxt.trim();
        if (!text) return;
        try { await api.post("/ai/voice-debrief", { job_id: id, transcript: text }); toast.success("Transcribed & sent to review"); setTranscript(""); refetch(); }
        catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
      }
    };
    recognitionRef.current = rec; rec.start(); setRecording(true);
    toast.info(auto ? "Listening… tap Stop to auto-send" : "Listening… speak your debrief");
  };
  const syncInvoice = async () => {
    try { const { data: r } = await api.post("/qbo/sync/invoice", { job_id: id }); toast.success(`Invoice synced to QuickBooks · #${r.qbo_invoice_id}`); refetch(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const total = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || 0), 0);
  const saveEstimate = async () => {
    try {
      const { data: est } = await api.post("/estimates", { job_id: id, line_items: lines, kind: "estimate" });
      toast.success("Estimate saved"); refetch(); return est;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const signLatest = async () => {
    const est = estimates.find((e) => e.status === "draft") || (await saveEstimate());
    if (!est) return;
    if (!signer || !sig) return toast.error("Signer name and signature required");
    const payload = { estimate_id: est.id, signer_name: signer, signature_image: sig,
      consent_text: "I approve the scope and pricing described in this estimate.",
      ip: "auto", device: navigator.userAgent.slice(0, 60), lat: 30.2672, lng: -97.7431 };
    try {
      const live = await runOrQueue("signature", payload, () => api.post("/estimates/sign", payload));
      toast[live ? "success" : "info"](live ? "Signature captured" : "Offline - signature queued");
      setSigner(""); setSig(""); if (live) refetch();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const takePayment = async (online) => {
    const payload = { job_id: id, kind: payKind, amount: Number(payAmt), online };
    try {
      const live = await runOrQueue("field_payment", payload, () => api.post("/billing/field-payment", payload));
      toast[live ? "success" : "info"](live ? (online ? "Payment captured" : "Queued offline (pending review)") : "Offline - payment queued");
      if (live) refetch();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      const payload = { job_id: id, filename: file.name, content_type: file.type, data: dataUrl };
      try {
        const live = await runOrQueue("photo_upload", payload, () => api.post("/files/upload", payload));
        toast[live ? "success" : "info"](live ? "Photo uploaded (checksummed + scanned)" : "Offline - photo queued");
        if (live) refetch();
      } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };
  const downloadBinder = async () => {
    setBinderBusy(true);
    try { const { data: b } = await api.get(`/jobs/${id}/binder`); generateBinder(b); toast.success("Binder PDF generated"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBinderBusy(false); }
  };
  const closeout = async (override = false) => {
    try {
      const { data: r } = await api.post(`/jobs/${id}/closeout${override ? "?override=true" : ""}`);
      toast.success(`Closed out · ${r.qbo_ref}`);
      // Auto-send the signed binder PDF to the customer + office.
      try {
        const { data: b } = await api.get(`/jobs/${id}/binder`);
        const base64 = binderDataUri(b).split(",")[1];
        const { data: sent } = await api.post(`/jobs/${id}/binder/send`, {
          pdf_base64: base64, filename: `SiteControl_JobBinder_${id.slice(0, 8)}.pdf`, checksum: b.checksum_sha256 });
        toast.success(`Binder emailed to ${sent.emailed_to.length} recipient(s)`);
      } catch { toast.warning("Closed out, but binder auto-send failed"); }
      refetch();
    } catch (e) {
      if (e.response?.status === 423 && !override && window.confirm(`${apiErr(e.response?.data?.detail)}\n\nLock and push the invoice anyway?`)) return closeout(true);
      toast.error(apiErr(e.response?.data?.detail));
    }
  };
  const saveChecklist = async () => {
    try { await api.patch(`/jobs/${id}/checklist`, { checklist }); toast.success("Checklist saved"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const saveSysState = async () => {
    try { await api.patch(`/jobs/${id}/system-state`, { fields: sysState }); toast.success("System state saved"); refetch(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const Card = ({ title, icon: Icon, children, testid }) => (
    <div className="card-tac p-5" data-testid={testid}>
      <div className="mb-4 flex items-center gap-2"><Icon className="h-4 w-4 text-cyan-400" /><h3 className="font-display font-semibold text-white">{title}</h3></div>
      {children}
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl p-6">
      <Eyebrow>{job.trade === "electrical" ? "Electrical" : "AV / Low-Voltage"}</Eyebrow>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold text-white" data-testid="jobdetail-title">{job.title}</h1>
        <StatusPill status={job.status} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-slate-400">
        <span>{job.customer_name}</span>
        {job.property_address && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.property_address}</span>}
        {job.qbo_ref && <span className="font-mono-tac text-xs text-purple-300">QBO {job.qbo_ref}</span>}
      </div>

      <JobCosting jobId={id} />

      <JobChecklist job={job} onChange={refetch} />

      {canOffice && <CrewAssignment jobId={id} onSaved={refetch} />}

      <LiveRollout job={job} onChange={refetch} />

      <AssetCapture jobId={id} />

      <ProjectShareBar job={job} />

      {canOffice && <PayoutWidget jobId={id} payout={job.payout} onSaved={refetch} />}

      {/* Status actions */}
      <div className="mt-6 flex flex-wrap gap-3">
        {NEXT[job.status] && (
          <button data-testid="advance-status-btn" onClick={advance} className="btn-amber rounded-full px-5 py-2.5 text-sm">
            Advance → {NEXT[job.status].replace(/_/g, " ")}
          </button>
        )}
        <button data-testid="safety-gate-btn" onClick={safety} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-white hover:border-emerald-500">
          <ShieldCheck className="h-4 w-4 text-emerald-400" /> {job.safety_gate ? "Safety cleared" : "Complete safety gate"}
        </button>
        {job.directions_url && (
          <a data-testid="directions-link" href={job.directions_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-white hover:border-cyan-500">
            <Navigation className="h-4 w-4 text-cyan-400" /> Directions
          </a>
        )}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <JobPhotoCapture jobId={id} />
        {/* OSHA pre-task safety lock */}
        <Card title="Pre-task safety (OSHA)" icon={ShieldAlert} testid="pretask-card">
          {job.pretask_lock ? (
            <div data-testid="pretask-cleared" className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-emerald-400"><ShieldCheck className="h-4 w-4" /> Pre-task locked in. Work may proceed.</div>
              <p className="font-mono-tac text-[11px] text-slate-500">signed by {job.pretask_lock.crew_lead || "crew lead"} · {String(job.pretask_lock.at || "").slice(0, 19)}</p>
              {job.pretask_lock.signature && <img src={job.pretask_lock.signature} alt="crew lead signature" className="mt-2 h-16 rounded-lg border border-slate-800 bg-[#0d1420]" />}
            </div>
          ) : (
            <>
              <p className="mb-2 text-xs text-slate-400">Acknowledge every item, then the crew lead signs to unlock "Start work" (in progress).</p>
              {(pretaskTpl?.items || []).map((it) => (
                <label key={it} data-testid={`pretask-${it}`} className="flex cursor-pointer items-center gap-2 py-1.5 text-sm text-slate-300">
                  <input type="checkbox" checked={!!pretask[it]} onChange={() => setPretask({ ...pretask, [it]: !pretask[it] })} className="accent-amber-500" />
                  <span className={pretask[it] ? "text-slate-500 line-through" : ""}>{it}</span>
                </label>
              ))}
              <input data-testid="pretask-crewlead-input" placeholder="Crew lead name" value={crewLead} onChange={(e) => setCrewLead(e.target.value)}
                className="mt-3 w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
              <div className="mt-2"><SignaturePad onCapture={setPretaskSig} /></div>
              <button data-testid="pretask-lock-btn" onClick={completePretask} className="btn-amber mt-3 rounded-full px-4 py-2 text-xs">Lock in pre-task</button>
            </>
          )}
        </Card>

        {/* Voice debrief */}
        <Card title="Voice debrief · AI extraction" icon={Mic} testid="voice-debrief-card">
          <textarea data-testid="voice-transcript-input" value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={4}
            placeholder="Tap record and speak, or paste the debrief transcript…"
            className="w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-sm text-white focus:border-amber-500 focus:outline-none" />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select data-testid="voice-lang-select" value={voiceLang} onChange={(e) => setVoiceLang(e.target.value)}
              className="rounded-full border border-slate-700 bg-[#0d1420] px-3 py-2 text-xs text-white focus:border-amber-500 focus:outline-none">
              <option value="en-US">English (US)</option>
              <option value="es-US">Español (US)</option>
              <option value="es-MX">Español (MX)</option>
              <option value="fr-CA">Français (CA)</option>
              <option value="pt-BR">Português (BR)</option>
            </select>
            <button data-testid="voice-record-btn" onClick={() => toggleRecord(false)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs ${recording ? "bg-rose-500 text-white animate-pulse" : "border border-slate-700 text-white hover:border-cyan-500"}`}>
              <Mic className="h-4 w-4" /> {recording ? "Stop recording" : "Record voice"}
            </button>
            <button data-testid="voice-autosend-btn" onClick={() => toggleRecord(true)} className="inline-flex items-center gap-2 rounded-full bg-cyan-500 px-4 py-2 text-xs font-semibold text-black hover:bg-cyan-400">
              <Mic className="h-4 w-4" /> Record &amp; auto-send
            </button>
            <button data-testid="voice-debrief-record-button" onClick={() => setTranscript(SAMPLE)} className="rounded-full border border-slate-700 px-4 py-2 text-xs text-white hover:border-cyan-500">Simulate</button>
            <button data-testid="voice-submit-btn" onClick={submitVoice} className="btn-amber rounded-full px-4 py-2 text-xs">Extract & send to review</button>
          </div>
          {ai_drafts.map((d) => (
            <div key={d.id} className="mt-4 rounded-xl border border-slate-800 bg-[#0d1420] p-3" data-testid={`ai-draft-${d.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">{d.source}</span>
                <span className="font-mono-tac text-xs text-cyan-400">conf {Math.round(d.confidence * 100)}%</span>
              </div>
              <StatusPill status={d.status === "needs_review" ? "needs_review" : "completed"} className="mt-2" />
              {d.reviewed_at && (
                <p className="mt-1 font-mono-tac text-[10px] text-slate-500" data-testid={`ai-draft-review-trail-${d.id}`}>
                  {d.status} by {d.reviewer_name || "reviewer"} · {String(d.reviewed_at).slice(0, 19).replace("T", " ")}
                </p>
              )}
              <pre className="mt-2 overflow-x-auto text-xs text-slate-300">{JSON.stringify(d.extracted, null, 2)}</pre>
            </div>
          ))}
        </Card>

        {/* Estimate + signature */}
        <Card title="Estimate & signature" icon={PenTool} testid="estimate-card">
          {lines.map((l, i) => (
            <div key={i} className="mb-2 flex gap-2">
              <input data-testid={`line-desc-${i}`} placeholder="Item" value={l.description} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                className="flex-1 rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
              <input type="number" placeholder="Qty" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))}
                className="w-16 rounded-lg border border-slate-700 bg-[#0d1420] px-2 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
              <input type="number" placeholder="$" value={l.unit_price} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))}
                className="w-20 rounded-lg border border-slate-700 bg-[#0d1420] px-2 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
              <button onClick={() => setLines(lines.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button data-testid="add-line-btn" onClick={() => setLines([...lines, { description: "", qty: 1, unit_price: 0 }])} className="inline-flex items-center gap-1 text-xs text-cyan-400"><Plus className="h-3 w-3" /> Add line</button>
          <div className="mt-3 flex items-center justify-between border-t border-slate-800 pt-3">
            <span className="text-sm text-slate-400">Total</span>
            <span className="font-display text-xl font-bold text-amber-400" data-testid="estimate-total">${total.toFixed(2)}</span>
          </div>
          <button data-testid="save-estimate-btn" onClick={saveEstimate} className="mt-3 w-full rounded-xl border border-slate-700 py-2.5 text-sm text-white hover:border-amber-500">Save estimate</button>

          <div className="mt-4">
            <input data-testid="signer-name-input" placeholder="Signer name" value={signer} onChange={(e) => setSigner(e.target.value)}
              className="mb-2 w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
            <SignaturePad onCapture={setSig} />
            <button data-testid="capture-signature-btn" onClick={signLatest} className="btn-amber mt-3 w-full rounded-xl py-2.5 text-sm">Capture signature</button>
          </div>
          {estimates.map((e) => (
            <div key={e.id} className="mt-3 flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2 text-sm" data-testid={`estimate-row-${e.id}`}>
              <span className="text-slate-300">${e.total?.toFixed(2)} · {e.kind}</span>
              <StatusPill status={e.status === "signed" ? "completed" : "scheduled"} />
            </div>
          ))}
        </Card>

        {/* Field payment */}
        <Card title="Field payment" icon={DollarSign} testid="field-payment-card">
          <div className="flex gap-2">
            <select data-testid="payment-kind-select" value={payKind} onChange={(e) => setPayKind(e.target.value)}
              className="flex-1 rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none">
              <option value="deposit">Deposit</option><option value="progress">Progress</option><option value="full">Full</option>
            </select>
            <input data-testid="payment-amount-input" type="number" placeholder="Amount" value={payAmt} onChange={(e) => setPayAmt(e.target.value)}
              className="w-28 rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
          </div>
          <div className="mt-3 flex gap-2">
            <button data-testid="take-payment-online-btn" onClick={() => takePayment(true)} className="btn-amber flex-1 rounded-xl py-2.5 text-sm">Capture online</button>
            <button data-testid="take-payment-offline-btn" onClick={() => takePayment(false)} className="flex-1 rounded-xl border border-slate-700 py-2.5 text-sm text-white hover:border-cyan-500">Queue offline</button>
          </div>
          {payments.map((p) => (
            <div key={p.id} className="mt-2 flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2 text-sm" data-testid={`payment-row-${p.id}`}>
              <span className="text-slate-300">${Number(p.amount).toFixed(2)} · {p.kind}</span>
              <span className={`font-mono-tac text-xs ${p.status === "captured" ? "text-emerald-400" : "text-amber-400"}`}>{p.status}</span>
            </div>
          ))}
        </Card>

        {/* Trade checklist */}
        <Card title="Trade checklist" icon={ListChecks} testid="checklist-card">
          {(template?.checklist || []).map((item) => (
            <label key={item} data-testid={`checklist-${item}`} className="flex cursor-pointer items-center gap-2 py-1.5 text-sm text-slate-300">
              <input type="checkbox" checked={!!checklist[item]} onChange={() => setChecklist({ ...checklist, [item]: !checklist[item] })} className="accent-amber-500" />
              <span className={checklist[item] ? "text-slate-500 line-through" : ""}>{item}</span>
            </label>
          ))}
          <button data-testid="save-checklist-btn" onClick={saveChecklist} className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-xs text-white hover:border-amber-500"><Save className="h-3.5 w-3.5" /> Save checklist</button>
        </Card>

        {/* System state (trade-specific) */}
        <Card title="System state" icon={FileCheck2} testid="system-state-card">
          {(template?.system_state_fields || []).map((f) => (
            <div key={f.key} className="mb-2">
              <label className="mb-1 block font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">{f.label}</label>
              {f.type === "bool" ? (
                <input type="checkbox" data-testid={`sysfield-${f.key}`} checked={!!sysState[f.key]} onChange={() => setSysState({ ...sysState, [f.key]: !sysState[f.key] })} className="accent-amber-500" />
              ) : (
                <input type={f.type === "number" ? "number" : "text"} data-testid={`sysfield-${f.key}`} value={sysState[f.key] ?? ""} onChange={(e) => setSysState({ ...sysState, [f.key]: e.target.value })}
                  className="w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
              )}
            </div>
          ))}
          <button data-testid="save-system-state-btn" onClick={saveSysState} className="mt-2 inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-xs text-white hover:border-cyan-500"><Save className="h-3.5 w-3.5" /> Save system state</button>
        </Card>

        {/* Site photos + media */}
        <Card title="Site photos" icon={Camera} testid="photos-card">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="photo-input" onChange={onPhoto} />
          <button data-testid="add-photo-btn" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-white hover:border-amber-500">
            <Camera className="h-4 w-4 text-cyan-400" /> Capture / upload photo
          </button>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {(media || []).map((m) => (
              <div key={m.id} className="overflow-hidden rounded-lg border border-slate-800" data-testid={`media-${m.id}`}>
                {m.data && <img src={m.data.startsWith("data:") ? m.data : `data:image/jpeg;base64,${m.data}`} alt={m.filename} className="h-20 w-full object-cover" />}
                <div className="truncate px-1 py-0.5 font-mono-tac text-[9px] text-emerald-400">{m.scan_status}</div>
              </div>
            ))}
            {!(media || []).length && <p className="col-span-3 text-sm text-slate-500">No photos yet.</p>}
          </div>
        </Card>

        {/* Closeout */}
        <Card title="Office closeout & binder" icon={FileCheck2} testid="closeout-card">
          <p className="text-sm text-slate-400">Approve to generate the immutable PDF package and sync the invoice to QuickBooks Online.</p>
          <button data-testid="download-binder-btn" onClick={downloadBinder} disabled={binderBusy}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 py-2.5 text-sm text-white hover:border-cyan-500 disabled:opacity-50">
            {binderBusy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-cyan-400" />} Download job binder PDF
          </button>
          {canOffice ? (
            <>
              <button data-testid="closeout-btn" onClick={closeout} disabled={job.closed_out} className="btn-amber mt-3 w-full rounded-xl py-3 text-sm disabled:opacity-50">
                {job.closed_out ? "Closed out ✓" : "Approve & generate PDF + QBO sync"}
              </button>
              <button data-testid="qbo-sync-invoice-btn" onClick={syncInvoice} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 py-2.5 text-sm text-white hover:border-emerald-500">
                Sync invoice to QuickBooks
              </button>
            </>
          ) : <p className="mt-3 font-mono-tac text-xs text-slate-500">Office/Admin approval required.</p>}
        </Card>
      </div>
    </div>
  );
}
